import { ChangeDetectorRef, Component, Inject, Input, forwardRef, ViewChild, ElementRef, AfterViewInit, inject, SimpleChanges, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { SvcMediaCarouselInternalItem } from '../interfaces/svc-media-carousel-item';
import { SvcMediaCarouselComponent } from '../svc-media-carousel.component';
import { fadeIn, fadeOut } from '../../animations/fade';
import { filter, fromEvent, Subject, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'svc-media-carousel-item',
  templateUrl: './svc-media-carousel-item.component.html',
  styleUrls: ['./svc-media-carousel-item.component.scss'],
  animations: [fadeIn, fadeOut],
  host: {
    '[class.svc-media-carousel-item]': 'true',
    '[class.svc-mci-one-by-one]': `parent.renderMode === 'one-by-one'`,
    '[class.svc-mci-empty-view]': '!item',
    '[class.svc-mci-dark]': 'parent.isDark',
  },
})
export class SvcMediaCarouselItemComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  readonly #elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly #cdr = inject(ChangeDetectorRef);

  #videoElement: HTMLVideoElement;

  @ViewChild('video') set videoWatch(videoRef: ElementRef<HTMLVideoElement>) {
    const video = videoRef?.nativeElement;
    this.#videoElement = video;
    if (this.item?.type === 'video') {
      this.item.fileLoading = !!video;
      this.autoPlayIfNeeded(video);
      this.#cdr.markForCheck();
    }
  }

  @ViewChild('img') set imgWatch(imgRef: ElementRef<HTMLImageElement>) {
    const img = imgRef?.nativeElement;
    if (img && this.item?.type === 'image') {
      const itemUrl = this.item?.url;
      const shouldSetSrc = !!itemUrl && img.src !== itemUrl;
      this.item.fileLoading = shouldSetSrc;
      if (shouldSetSrc) {
        img.src = itemUrl;
      }
      this.#cdr.markForCheck();
    }
  }

  @Input() item: SvcMediaCarouselInternalItem;

  // Backed by a setter so async mutations (scroll/resize/needToTick happen outside an event
  // handler) still re-render when this component lives under an OnPush ancestor.
  #canBeDisplayed = false;
  protected get canBeDisplayed(): boolean {
    return this.#canBeDisplayed;
  }
  protected set canBeDisplayed(value: boolean) {
    if (value === this.#canBeDisplayed) return;
    this.#canBeDisplayed = value;
    this.#cdr.markForCheck();
  }
  protected get element(): HTMLElement {
    return this.#elementRef?.nativeElement;
  }
  protected get viewportEl(): HTMLElement {
    return this.parent.element.querySelector('.svc-mc-viewport');
  }

  #destroy = new Subject<void>();
  #wasInitialized = false;

  get enableAddButton(): boolean {
    const emptyView = this.parent.emptyView;
    const editMode = this.parent.editMode;
    const maxItems = this.parent.maxItems;
    const length = this.parent.items.length;
    const hasUrl = !this.parent.items[maxItems - 1]?.url;

    return emptyView && editMode && (maxItems <= 0 || length < maxItems || hasUrl);
  }

  constructor(
    @Inject(forwardRef(() => SvcMediaCarouselComponent))
    public parent: SvcMediaCarouselComponent
  ) {}

  public ngOnInit(): void {
    this.parent.onCurrentItemChanged
      .pipe(
        takeUntil(this.#destroy),
        filter((item) => !!item),
        tap((current) => {
          if (current === this.item) {
            this.canBeDisplayed = true;
            this.autoPlayIfNeeded(this.#videoElement);
          } else if (this.item?.type === 'video') {
            // AB#72795 pause the video that stops being the current slide, with or without autoPlayVideo
            this.#videoElement?.pause();
          }
        })
      )
      .subscribe();
    this.parent.needToTickItems$
      .pipe(
        takeUntil(this.#destroy),
        tap(() => {
          if (this.parent.renderModeIsList) {
            this.onViewportScroll();
          } else if (this.item && this.parent.getCurrent() === this.item) {
            this.canBeDisplayed = true;
          }
        })
      )
      .subscribe();
    this.#wasInitialized = true;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('item' in changes && this.item) {
      const previousUrl = changes.item?.previousValue?.url;
      this.item.fileLoading = this.item?.type === 'image' && !!this.item?.url && this.item.url !== previousUrl;
    }

    if (this.#wasInitialized && this.viewportEl) {
      this.onViewportScroll();
    }
  }

  public ngAfterViewInit(): void {
    if (this.viewportEl) {
      [fromEvent(this.viewportEl, 'scroll'), fromEvent(window, 'resize')].forEach((observ) =>
        observ
          .pipe(
            takeUntil(this.#destroy),
            filter(() => this.parent.renderModeIsList),
            tap(() => this.onViewportScroll())
          )
          .subscribe()
      );
      this.onViewportScroll();
    }
    if (!this.parent.renderModeIsList && !this.canBeDisplayed) {
      this.canBeDisplayed = this.item && this.parent.getCurrent() === this.item;
    }
  }

  private onViewportScroll(): void {
    setTimeout(() => {
      if (!this.parent.renderModeIsList || !this.item || this.canBeDisplayed || !this.viewportEl) return;
      const viewport = this.viewportEl;
      const viewportScrollLeft = viewport.scrollLeft;
      const viewportWidth = viewport.offsetWidth;
      this.canBeDisplayed = viewportScrollLeft + viewportWidth >= this.element.offsetLeft;
    });
  }

  private autoPlayIfNeeded(video: HTMLVideoElement): void {
    if (!video || this.item?.type !== 'video' || !this.parent.autoPlayVideo) return;

    video.controls = this.parent.videoControls;
    video.muted = true;
    video.play().catch(() => undefined);
  }

  protected onVideoMouseEnter(element: HTMLVideoElement): void {
    if (!this.parent.playVideoWhenMouseHover || this.parent.editMode) {
      if (this.parent.videoControls) {
        element.controls = true;
      }
    } else if (this.parent.playVideoWhenMouseHover && !this.parent.editMode) {
      element.play();
      element.muted = true;
    }
  }

  protected onVideoMouseLeave(element: HTMLVideoElement): void {
    if (!this.parent.playVideoWhenMouseHover || this.parent.editMode) {
      if (this.parent.videoControls) {
        element.controls = false;
      }
    } else if (this.parent.playVideoWhenMouseHover && !this.parent.editMode) {
      element.muted = false;
      element.pause();
      element.currentTime = 0;
    }
  }

  public ngOnDestroy(): void {
    if (!this.item) {
      this.parent.needToTickItems$.next();
    }
    this.#destroy.next();
    this.#destroy.complete();
  }
}
